import React, { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { getServiceTrackingStatus } from "../api/apiCalls";
import { trackEvent } from "../utils/analytics";
import sifraImage from "../assets/sifra.png";

const STATUS_LABELS = {
  received: "Вашиот уред е примен на сервис",
  "in-progress": "Уредот е во процес на сервисирање",
  completed: "Уредот е сервисиран и подготвен за подигнување",
};

const EXCLUDED_KEYS = new Set([
  "trackingId",
  "deviceModel",
  "status",
  "issueDescription",
  "issueDecprtion",
  "_id",
  "__v",
]);

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => formatValue(item)).filter(Boolean).join(", ");
  }

  if (typeof value === "object") {
    return Object.values(value)
      .map((item) => formatValue(item))
      .filter(Boolean)
      .join(", ");
  }

  return String(value);
};

const toTitleCase = (value) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (match) => match.toUpperCase());

const getStatusTone = (value) => {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === "completed") {
    return "success";
  }

  if (normalized === "in-progress") {
    return "warning";
  }

  if (normalized === "received") {
    return "info";
  }

  return "neutral";
};

function ServiceStatusView() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryTrack = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("track") || "";
  }, [location.search]);

  const [trackId, setTrackId] = useState(queryTrack);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(Boolean(queryTrack));

  useEffect(() => {
    document.title = "Buzz Mobile - Статус на уред";
  }, []);

  useEffect(() => {
    setTrackId(queryTrack);
  }, [queryTrack]);

  useEffect(() => {
    if (!queryTrack) {
      setResult(null);
      setError("");
      setHasSearched(false);
      return;
    }

    const loadStatus = async () => {
      setHasSearched(true);
      setLoading(true);
      setError("");

      const response = await getServiceTrackingStatus(queryTrack);

      if (!response.ok) {
        trackEvent("service_status_result", {
          event_category: "service_status",
          result: response.status === 404 ? "not_found" : "error",
        });
        setResult(null);
        setError(
          response.status === 404
            ? "Не постои сервис на уред со внесената шифра."
            : "Настана грешка при пребарувањето."
        );
        setLoading(false);
        return;
      }

      const normalizedData =
        response.data?.service ||
        response.data?.data ||
        response.data?.result ||
        response.data;

      trackEvent("service_status_result", {
        event_category: "service_status",
        result: "success",
        status: normalizedData?.status || "unknown",
      });
      setResult(normalizedData && typeof normalizedData === "object" ? normalizedData : null);
      setLoading(false);
    };

    loadStatus();
  }, [queryTrack]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedTrackId = trackId.trim();

    if (!trimmedTrackId) {
      setResult(null);
      setError("Внесете шифра.");
      setHasSearched(false);
      navigate("/status", { replace: true });
      return;
    }

    setHasSearched(true);
    trackEvent("service_status_check", {
      event_category: "service_status",
      method: "manual_submit",
    });
    navigate(`/status?track=${encodeURIComponent(trimmedTrackId)}`, {
      replace: true,
    });
  };

  const extraDetails = result
    ? Object.entries(result)
        .filter(([key, value]) => !EXCLUDED_KEYS.has(key) && formatValue(value))
        .map(([key, value]) => ({
          label: toTitleCase(key),
          value: formatValue(value),
        }))
    : [];

  const statusTone = getStatusTone(result?.status);
  const statusLabel =
    STATUS_LABELS[String(result?.status || "").toLowerCase()] || result?.status || "";
  const issueDescription =
    result?.issueDescription || result?.issueDecprtion || "";
  const StatusIcon =
    statusTone === "success"
      ? CheckCircleOutlineRoundedIcon
      : statusTone === "warning"
        ? BuildCircleOutlinedIcon
        : InfoOutlinedIcon;

  return (
    <div className="status-page">
      <div className="container status-page__content">
        <section className="status-layout">
          <div className="status-card status-card--form">
            <div className="status-card__heading">
              <span className="status-card__eyebrow">Статус на уред</span>
              <div className="status-form__hint">
                <p>
                  Шифрата за проверка можете да ја најдете на сервисниот
                  документ.
                </p>
                <img
                  src={sifraImage}
                  alt="Пример каде се наоѓа шифрата на сервисниот документ"
                />
              </div>
            </div>

            <form className="status-form" onSubmit={handleSubmit}>
              <div className="status-form__field">
                <SearchOutlinedIcon />
                <TextField
                  placeholder="Внесете ја шифрата"
                  label="Внесете ја шифрата"
                  variant="outlined"
                  fullWidth
                  value={trackId}
                  onChange={(event) => setTrackId(event.target.value)}
                />
              </div>

              <Button
                type="submit"
                variant="contained"
                className="status-form__submit"
                disabled={loading}
              >
                {loading ? "Се пребарува..." : "Провери статус"}
              </Button>
            </form>

            {error ? <p className="status-form__error">{error}</p> : null}

            {hasSearched && loading ? (
              <div className="status-inline status-empty">
                <AutorenewRoundedIcon className="status-empty__spin" />
                <p>Се пребарува уредот...</p>
              </div>
            ) : null}

            {hasSearched && !loading && result ? (
              <div className="status-inline status-result">
                <div className="status-result__summary">
                  <div className="status-result__top">
                    <div className="status-result__device">
                      <span>Име на уред:</span>
                      <h2>{result?.deviceModel || "Уред"}</h2>
                    </div>
                    <div className="status-result__device">
                      <span>Опис на проблем</span>
                      <p>{issueDescription || "Нема опис"}</p>
                    </div>
                  </div>
                  <div className="status-result__status">
                    <span
                      className={`status-badge status-badge--${statusTone}`}
                    >
                      <StatusIcon fontSize="small" />
                      <span>
                        <b>Статус</b>: {statusLabel}
                      </span>
                    </span>
                  </div>
                </div>

                {extraDetails.length > 0 ? (
                  <div className="status-meta">
                    {extraDetails.map((item) => (
                      <div className="status-meta__row" key={item.label}>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

export default ServiceStatusView;
